import React from "react";
import {
  Box,
  Column,
  Columns,
  Stack,
  StackSection,
  Text,
} from "@origin-digital/ods-core";
import { CantFind } from "../../components/SearchResults/CantFind";
import { CantFindSkeleton } from "../../components/SearchResults/CantFindSkeleton";
import { FilterChips } from "../../components/SearchResults/FilterChips";
import { FilterChipsSkeleton } from "../../components/SearchResults/FilterChipsSkeleton";
import { NoResults } from "../../components/SearchResults/NoResults";
import { SearchPagination } from "../../components/SearchResults/SearchPagination";
import { SearchResultsList } from "../../components/SearchResults/SearchResultsList";
import { SearchResultsSkeleton } from "../../components/SearchResults/SearchResultsSkeleton";
import { ErrorBoundary } from "../../components/ErrorBoundary/ErrorBoundary";
import { useSearchResultsPage } from "./hooks/useSearchResultsPage";
import { PageTemplate } from "../PageTemplate/PageTemplate";

export const SearchResultsPage: React.FC = () => {
  const {
    tabs,
    results,
    pagination,
    relatedSearches,
    spellingSuggestion,
    error,
    selectedTab,
    showSkeleton,
    handleSelect,
    setCurrentPage,
  } = useSearchResultsPage();

  return (
    <PageTemplate data-id="search-results-page">
      <StackSection
        data-id="search-results-content"
        width="xlarge"
        hideGutter={false}
        space="none"
        paddingY="none"
        backgroundColor="white"
      >
        <Box marginBottom={{ xs: "xxlarge", lg: "xxxlarge" }}>
          <Stack space={{ xs: "xxlarge", lg: "xxxlarge" }}>
            {showSkeleton ? (
              <FilterChipsSkeleton />
            ) : (
              <FilterChips
                tabs={tabs}
                selectedTab={selectedTab}
                onSelect={handleSelect}
              />
            )}
            {!showSkeleton && !error && results.length === 0 ? (
              <NoResults spellingSuggestion={spellingSuggestion} />
            ) : (
              <>
                {showSkeleton || relatedSearches.length > 0 ? (
                  <Columns space="large" collapseBelow="md">
                    <Column width="3/4">
                      <ErrorBoundary
                        fallback={
                          <Text>Something went wrong. Please try again.</Text>
                        }
                      >
                        {showSkeleton && <SearchResultsSkeleton />}
                        {!showSkeleton && !error && results.length > 0 && (
                          <SearchResultsList results={results} />
                        )}
                      </ErrorBoundary>
                    </Column>
                    <Column width="1/4">
                      {showSkeleton ? (
                        <CantFindSkeleton />
                      ) : (
                        <CantFind relatedSearches={relatedSearches} />
                      )}
                    </Column>
                  </Columns>
                ) : (
                  <ErrorBoundary
                    fallback={
                      <Text>Something went wrong. Please try again.</Text>
                    }
                  >
                    {!error && results.length > 0 && (
                      <SearchResultsList results={results} />
                    )}
                  </ErrorBoundary>
                )}
                {!showSkeleton &&
                  !error &&
                  pagination?.pages &&
                  pagination.pages.length > 0 && (
                    <SearchPagination
                      pages={pagination.pages}
                      onSelect={setCurrentPage}
                    />
                  )}
              </>
            )}
          </Stack>
        </Box>
      </StackSection>
    </PageTemplate>
  );
};
